const STORE_CONTEXTS = {
  fashion: { category: 'Fashion' },
  tech: { category: 'Tech' },
  home: { category: 'Home' },
  beauty: { category: 'Beauty' },
  'kids-baby': { category: 'Kids & Baby' },
  'sports-outdoors': { category: 'Sports & Outdoors' }
};

const NEUTRAL_CONTEXT = { category: 'all' };

const T = (k) => (window.KurlectiveI18n || {})[k] || '';

const categorySlugFor = (category) => {
  const match = Object.entries(STORE_CONTEXTS).find(([, context]) => context.category === category);
  return match?.[0] || null;
};

const categoryKey = (category) => (categorySlugFor(category) || '').replace('-', '_');
const categoryLabel = (category) => T(`cat_${categoryKey(category)}`);
const countLabel = (n, key) => {
  const single = Number(n) === 1;
  return T(`${key}_${single ? 'one' : 'other'}`).replace('[count]', n);
};
const productCount = (n) => countLabel(n, 'product_count');

const categoriesFor = (element) => (element?.dataset.storeCategories || 'Other').split('|').filter(Boolean);
const matchesCategory = (element, category) => category === 'all' || categoriesFor(element).includes(category);

const setVisibility = (element, show) => {
  if (!element) return;
  element.hidden = !show;
  element.classList.toggle('is-filtered-out', !show);
};

const initFeaturedStores = (hub, directoryRows) => {
  const root = hub.querySelector('[data-featured-stores]');
  if (!root) return { setContext: () => {} };

  const preview = root.querySelector('[data-featured-preview]');
  const media = preview?.querySelector('.featured-store-preview-media');
  const image = media?.querySelector('img');
  const placeholder = media?.querySelector('[data-featured-preview-placeholder]');
  const name = preview?.querySelector('[data-featured-preview-name]');
  const description = preview?.querySelector('[data-featured-preview-description]');
  const count = preview?.querySelector('[data-featured-preview-count]');
  const kicker = preview?.querySelector('[data-featured-preview-kicker]');
  const contextLabel = root.querySelector('[data-featured-context-label]');
  const list = root.querySelector('[data-featured-list]');
  const curatedRows = Array.from(root.querySelectorAll('[data-featured-store-row][data-featured-curated="true"]'));
  let imageRequest = 0;

  const showPlaceholder = (storeName) => {
    if (image) {
      image.hidden = true;
      image.alt = '';
    }
    if (placeholder) {
      placeholder.textContent = (storeName || 'Store').slice(0, 2).toUpperCase();
      placeholder.hidden = false;
    }
  };

  const showImage = (url, storeName) => {
    if (!image || !url) {
      if (image) {
        image.removeAttribute('src');
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
      }
      showPlaceholder(storeName);
      return;
    }

    imageRequest += 1;
    const requestId = imageRequest;
    const source = url.trim();
    showPlaceholder(storeName);

    image.onload = () => {
      if (requestId !== imageRequest) return;
      if (image.naturalWidth > 0) {
        image.hidden = false;
        image.alt = storeName;
        if (placeholder) placeholder.hidden = true;
      }
    };

    image.onerror = () => {
      if (requestId !== imageRequest) return;
      image.removeAttribute('src');
      showPlaceholder(storeName);
    };

    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = source;

    if (image.complete) {
      if (image.naturalWidth > 0) image.onload();
      else image.onerror();
    }
  };

  const activate = (row) => {
    if (!row || !preview) return;
    Array.from(list?.querySelectorAll('[data-featured-store-row]') || []).forEach((candidate) => {
      candidate.classList.toggle('active', candidate === row);
    });

    const storeName = row.dataset.storeName || '';
    preview.href = row.dataset.storeUrl || row.href;
    if (name) name.textContent = storeName;
    if (description) description.textContent = row.dataset.storeDescription || T('explore_products_from').replace('[store]', storeName);
    if (count) count.textContent = productCount(row.dataset.storeCount || 0);
    showImage(row.dataset.storeImage || '', storeName);
  };

  const buildGeneratedRow = (store) => {
    const row = document.createElement('a');
    row.className = 'featured-store-row';
    row.href = store.url;
    row.dataset.featuredStoreRow = '';
    row.dataset.featuredGenerated = 'true';
    row.dataset.storeName = store.name;
    row.dataset.storeDescription = store.description;
    row.dataset.storeCount = store.count;
    row.dataset.storeUrl = store.url;
    row.dataset.storeImage = store.image;
    row.dataset.storeCategories = store.categories.join('|');

    const index = document.createElement('span');
    index.className = 'featured-store-row-index';
    const title = document.createElement('strong');
    title.textContent = store.name;
    const products = document.createElement('span');
    products.textContent = productCount(store.count);
    const arrow = document.createElement('b');
    arrow.textContent = '↗';
    row.append(index, title, products, arrow);
    return row;
  };

  const storeRegistry = directoryRows.map((row) => ({
    name: row.dataset.storeName || '',
    description: row.dataset.storeDescription || '',
    count: row.dataset.storeCount || '0',
    url: row.dataset.storeUrl || row.href,
    image: row.dataset.storeImage || '',
    categories: categoriesFor(row)
  }));

  list?.addEventListener('mouseover', (event) => {
    const row = event.target.closest('[data-featured-store-row]');
    if (row && list.contains(row) && !row.hidden) activate(row);
  });
  list?.addEventListener('focusin', (event) => {
    const row = event.target.closest('[data-featured-store-row]');
    if (row && list.contains(row) && !row.hidden) activate(row);
  });

  const setContext = (category = 'all') => {
    list?.querySelectorAll('[data-featured-generated="true"]').forEach((row) => row.remove());
    const selectedNames = new Set();
    const visibleRows = [];

    curatedRows.forEach((row) => {
      const show = matchesCategory(row, category);
      setVisibility(row, show);
      if (show) {
        selectedNames.add((row.dataset.storeName || '').toLocaleLowerCase());
        visibleRows.push(row);
      }
    });

    if (category !== 'all' && list) {
      storeRegistry
        .filter((store) => store.categories.includes(category) && !selectedNames.has(store.name.toLocaleLowerCase()))
        .slice(0, Math.max(0, 6 - visibleRows.length))
        .forEach((store) => {
          const row = buildGeneratedRow(store);
          list.appendChild(row);
          visibleRows.push(row);
          selectedNames.add(store.name.toLocaleLowerCase());
        });
    }

    visibleRows.slice(6).forEach((row) => setVisibility(row, false));
    const finalRows = visibleRows.slice(0, 6);
    finalRows.forEach((row, index) => {
      setVisibility(row, true);
      const number = row.querySelector('.featured-store-row-index');
      if (number) number.textContent = String(index + 1).padStart(2, '0');
    });

    if (contextLabel) contextLabel.textContent = category === 'all' ? T('selected_by') : T('category_stores').replace('[category]', categoryLabel(category));
    if (kicker) kicker.textContent = category === 'all' ? T('featured_store') : T('category_store').replace('[category]', categoryLabel(category));
    if (finalRows[0]) activate(finalRows[0]);
  };

  return { setContext };
};

const initStoreHub = (hub) => {
  if (hub.dataset.storeHubReady === 'true') return;
  hub.dataset.storeHubReady = 'true';

  const heroEyebrow = hub.querySelector('[data-store-hero-eyebrow]');
  const heroTitle = hub.querySelector('[data-store-hero-title]');
  const heroCopy = hub.querySelector('[data-store-hero-copy]');
  const directory = hub.querySelector('[data-store-directory]');
  if (!directory) return;

  const rows = Array.from(directory.querySelectorAll('[data-store-row]'));
  const groups = Array.from(directory.querySelectorAll('[data-store-group]'));
  const letterButtons = Array.from(directory.querySelectorAll('[data-store-letter]'));
  const categoryLinks = Array.from(hub.querySelectorAll('[data-store-category-filter]'));
  const input = directory.querySelector('[data-store-search]');
  const count = directory.querySelector('[data-store-visible-count]');
  const empty = directory.querySelector('[data-store-empty]');
  const categoryContext = directory.querySelector('[data-store-category-context]');
  const activeCategoryLabel = directory.querySelector('[data-store-active-category]');
  const clearCategory = directory.querySelector('[data-store-category-clear]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const featured = initFeaturedStores(hub, rows);
  let activeLetter = 'all';
  let activeCategory = 'all';

  if (empty) {
    empty.setAttribute('role', 'status');
    empty.setAttribute('aria-live', 'polite');
  }

  const setActiveLetter = () => {
    letterButtons.forEach((button) => button.classList.toggle('active', button.dataset.storeLetter === activeLetter));
  };

  const setCategoryState = () => {
    categoryLinks.forEach((link) => link.classList.toggle('active', link.dataset.storeCategoryFilter === activeCategory));
    if (categoryContext) categoryContext.hidden = activeCategory === 'all';
    if (activeCategoryLabel) activeCategoryLabel.textContent = activeCategory === 'all' ? '' : categoryLabel(activeCategory);
  };

  const setLetterAvailability = () => {
    letterButtons.forEach((button) => {
      const value = button.dataset.storeLetter;
      if (value === 'all') {
        button.disabled = false;
        return;
      }
      button.disabled = !rows.some((row) => matchesCategory(row, activeCategory) && row.dataset.storeLetter === value);
    });
  };

  const refreshDirectory = () => {
    const query = (input?.value || '').trim().toLocaleLowerCase();
    let visible = 0;

    rows.forEach((row) => {
      const name = (row.dataset.storeName || '').toLocaleLowerCase();
      const letterMatches = activeLetter === 'all' || row.dataset.storeLetter === activeLetter;
      const searchMatches = !query || name.includes(query);
      const show = matchesCategory(row, activeCategory) && letterMatches && searchMatches;
      setVisibility(row, show);
      if (show) visible += 1;
    });

    groups.forEach((group) => {
      const hasVisibleRows = Array.from(group.querySelectorAll('[data-store-row]')).some((row) => !row.classList.contains('is-filtered-out'));
      setVisibility(group, hasVisibleRows);
    });

    setLetterAvailability();
    if (count) count.textContent = countLabel(visible, 'store_count');
    setVisibility(empty, visible === 0);
  };

  const contextSlugFromUrl = () => {
    const slug = new URL(window.location.href).searchParams.get('category');
    return slug && STORE_CONTEXTS[slug] ? slug : null;
  };

  // Path+search of the last state handled, so hash-only popstates (skip links) keep search and letter filters.
  let currentRoute = window.location.pathname + window.location.search;

  const writeContextUrl = (slug) => {
    const url = new URL(window.location.href);
    if (slug) url.searchParams.set('category', slug);
    else url.searchParams.delete('category');
    url.hash = '';
    window.history.pushState({ storeCategory: slug }, '', `${url.pathname}${url.search}${url.hash}`);
    currentRoute = url.pathname + url.search;
  };

  const applyContext = (slug, { scroll = false, updateUrl = false } = {}) => {
    const context = slug && STORE_CONTEXTS[slug] ? STORE_CONTEXTS[slug] : NEUTRAL_CONTEXT;
    activeCategory = context.category;
    activeLetter = 'all';
    if (input) input.value = '';

    const label = categoryLabel(context.category);
    const neutral = context === NEUTRAL_CONTEXT;
    if (heroEyebrow) heroEyebrow.textContent = neutral ? T('stores_eyebrow') : T('stores_hero_eyebrow').replace('[category]', label.toLocaleUpperCase());
    if (heroTitle) heroTitle.textContent = neutral ? T('stores_title') : T('stores_hero_title').replace('[category]', label);
    if (heroCopy) heroCopy.textContent = T(neutral ? 'stores_copy' : `stores_copy_${categoryKey(context.category)}`);

    setActiveLetter();
    setCategoryState();
    featured.setContext(activeCategory);
    refreshDirectory();

    if (updateUrl) writeContextUrl(slug && STORE_CONTEXTS[slug] ? slug : null);
    if (scroll) requestAnimationFrame(() => directory.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' }));
  };

  categoryLinks.forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      const slug = link.dataset.storeCategorySlug || categorySlugFor(link.dataset.storeCategoryFilter || '');
      if (slug) applyContext(slug, { scroll: true, updateUrl: true });
    });
  });

  clearCategory?.addEventListener('click', () => applyContext(null, { scroll: false, updateUrl: true }));

  letterButtons.forEach((button) => {
    button.addEventListener('click', () => {
      if (button.disabled) return;
      activeLetter = button.dataset.storeLetter || 'all';
      if (input) input.value = '';
      setActiveLetter();
      refreshDirectory();
    });
  });

  input?.addEventListener('input', () => {
    activeLetter = 'all';
    setActiveLetter();
    refreshDirectory();
  });

  window.addEventListener('popstate', () => {
    const route = window.location.pathname + window.location.search;
    if (route === currentRoute) return;
    currentRoute = route;
    applyContext(contextSlugFromUrl(), { scroll: false, updateUrl: false });
  });
  applyContext(contextSlugFromUrl(), { scroll: false, updateUrl: false });
};

document.querySelectorAll('[data-store-hub]').forEach(initStoreHub);
